import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridRowParams,
  GridActionsCellItem,
  GridApi,
  GridFilterModel,
  GridFilterItem,
} from '@mui/x-data-grid'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
} from '@mui/material'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { treasurySpanishLocale } from '../../utils/localization'
import { CustomToolbar, useDataGridSettings } from './CustomToolbar'



export interface SelectionAction {
  label: string
  icon?: React.ReactNode
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
  variant?: 'contained' | 'outlined' | 'text'
  disabled?: boolean
  onClick: (selectedRows: any[], selectedIds: any[]) => void
}

export interface RowAction {
  label: string
  icon?: React.ReactNode
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
  onClick: (row: any) => void
  show?: (row: any) => boolean
}

export interface SelectableDataGridProps extends Omit<DataGridProps, 'columns' | 'rowSelectionModel'> {
  columns: GridColDef[]
  title?: string
  selectionActions?: SelectionAction[]
  rowActions?: RowAction[]
  maxSelection?: number
  showSelectionSummary?: boolean
  showToolbar?: boolean
  allowSelectAll?: boolean
  selectionMessage?: string
  onSelectionChange?: (selectedRows: any[], selectedIds: any[]) => void
  rowSelectionModel?: GridRowSelectionModel
  /** Use custom toolbar with enhanced features */
  useCustomToolbar?: boolean
  /** Show filter bar in custom toolbar */
  showFilterBar?: boolean
  /** Show settings menu in custom toolbar */
  showSettings?: boolean
}

export const SelectableDataGrid: React.FC<SelectableDataGridProps> = ({
  columns,
  title,
  selectionActions = [],
  rowActions = [],
  maxSelection,
  showSelectionSummary = true,
  showToolbar = true,
  allowSelectAll = true,
  selectionMessage,
  onSelectionChange,
  rows = [],
  rowSelectionModel,
  useCustomToolbar = true,
  showFilterBar = true,
  showSettings = true,
  ...dataGridProps
}) => {
  const apiRef = useRef<GridApi>(null!)
  // MUI X v8.8.0 PURE UNCONTROLLED SELECTION PATTERN
  // No internal selectionModel state - let DataGrid manage its own selection
  const [currentSelection, setCurrentSelection] = useState<any[]>([])
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] })

  // Custom toolbar settings
  const [toolbarSettings, setToolbarSettings] = useDataGridSettings(
    `${title?.replace(/\s+/g, '-').toLowerCase() || 'selectable'}-toolbar-settings`
  )

  // Filter removal handler for custom toolbar
  const handleRemoveFilter = useCallback((filterId: GridFilterItem['id']) => {
    const newFilterModel: GridFilterModel = {
      items: filterModel.items.filter((item) => item.id !== filterId),
    }
    setFilterModel(newFilterModel)
  }, [filterModel])

  // Reset selection when rows change (defensive programming)
  useEffect(() => {
    if (rows.length === 0) {
      setCurrentSelection([])
    }
  }, [rows])

  // MUI X v8.8.0 UNCONTROLLED SELECTION HANDLER
  const handleSelectionChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      console.log('🔥 SELECTION CHANGE FIRED:', newSelectionModel);
      
      // MUI X v8.8.0 uses {type: 'include', ids: Set(...)} structure
      let selectedIds: any[] = [];
      
      if (Array.isArray(newSelectionModel)) {
        // Legacy format (array of IDs)
        selectedIds = newSelectionModel;
      } else if (newSelectionModel && typeof newSelectionModel === 'object' && 'ids' in newSelectionModel) {
        // MUI X v8.8.0 format {type: 'include', ids: Set(...)}
        const idsSet = (newSelectionModel as any).ids;
        if (idsSet instanceof Set) {
          selectedIds = Array.from(idsSet);
        } else if (Array.isArray(idsSet)) {
          selectedIds = idsSet;
        }
      }
      
      console.log('🆔 EXTRACTED IDS:', selectedIds);
      
      // Update our tracking state
      setCurrentSelection(selectedIds)
      
      // Filter selected rows with defensive programming
      if (onSelectionChange && Array.isArray(rows)) {
        const selectedRows = rows.filter((row) => 
          row && row.id !== undefined && selectedIds.includes(row.id)
        );
        console.log('🎯 SELECTED ROWS:', selectedRows.length, 'out of', rows.length);
        onSelectionChange(selectedRows, selectedIds)
      }
    },
    [onSelectionChange, rows]
  )

  // Get selected rows data - using currentSelection tracking
  const selectedRows = useMemo(() => {
    const result = rows.filter((row) => 
      row && row.id !== undefined && currentSelection.includes(row.id)
    );
    console.log('📊 SELECTION STATE:', currentSelection.length, 'IDs selected,', result.length, 'rows found');
    return result;
  }, [rows, currentSelection])


  // Enhanced columns with row actions
  const enhancedColumns = useMemo(() => {
    const cols = [...columns]

    if (rowActions.length > 0) {
      cols.push({
        field: 'actions',
        headerName: 'Acciones',
        type: 'actions',
        width: 80,
        getActions: (params: GridRowParams) => {
          if (params.row.isGroupHeader) return []
          
          return rowActions
            .filter(action => !action.show || action.show(params.row))
            .map((action) => {
              return (
                <GridActionsCellItem
                  key={action.label}
                  icon={(action.icon as React.ReactElement) || <VisibilityIcon />}
                  label={action.label}
                  onClick={() => action.onClick(params.row)}
                  showInMenu={false}
                />
              )
            })
        },
      })
    }

    return cols
  }, [columns, rowActions])



  return (
    <Stack spacing={2}>

      {/* Selection Summary and Actions */}
      {showSelectionSummary && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={currentSelection.length > 0 ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                  label={`${currentSelection.length} seleccionado${currentSelection.length !== 1 ? 's' : ''}`}
                  color={currentSelection.length > 0 ? 'primary' : 'default'}
                  variant={currentSelection.length > 0 ? 'filled' : 'outlined'}
                  size="medium"
                />
                
                {/* Selection Actions - moved next to chip */}
                {selectionActions.length > 0 && currentSelection.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectionActions.map((action, index) => (
                      <Button
                        key={index}
                        variant={action.variant || 'contained'}
                        color={action.color || 'success'}
                        startIcon={action.icon}
                        disabled={action.disabled}
                        onClick={() => action.onClick(selectedRows, currentSelection)}
                        size="medium"
                        sx={{ 
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          boxShadow: 2,
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Stack>
                )}
                
                <Typography variant="body2" color="text.secondary">
                  Seleccione filas usando las casillas de verificación
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                {rows.filter(r => !r.isGroupHeader).length} registro{rows.filter(r => !r.isGroupHeader).length !== 1 ? 's' : ''} total{rows.filter(r => !r.isGroupHeader).length !== 1 ? 'es' : ''}
              </Typography>
            </Box>

            {/* Selection Message/Warning */}
            {selectionMessage && currentSelection.length > 0 && (
              <Alert severity="info" variant="outlined">
                {selectionMessage}
              </Alert>
            )}

            {/* Max Selection Warning */}
            {maxSelection && currentSelection.length >= maxSelection && (
              <Alert severity="warning" variant="outlined">
                Máximo {maxSelection} elemento{maxSelection !== 1 ? 's' : ''} permitido{maxSelection !== 1 ? 's' : ''} para selección.
              </Alert>
            )}
          </Stack>
        </Paper>
      )}

      {/* Data Grid - MUI X v8.8.0 PURE UNCONTROLLED PATTERN */}
      <Paper elevation={1} sx={{ height: 600, overflow: 'hidden' }}>
        <DataGrid
          apiRef={apiRef}
          columns={enhancedColumns}
          rows={rows}
          density={useCustomToolbar ? (toolbarSettings.density || 'compact') : 'compact'}
          showCellVerticalBorder={useCustomToolbar ? toolbarSettings.showCellBorders : false}
          showColumnVerticalBorder={useCustomToolbar ? toolbarSettings.showColumnBorders : false}
          checkboxSelection
          onRowSelectionModelChange={handleSelectionChange}
          keepNonExistentRowsSelected
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pageSizeOptions={[10, 25, 50, 100]}
          localeText={treasurySpanishLocale}
          showToolbar={showToolbar}
          {...(useCustomToolbar ? {
            slots: {
              toolbar: CustomToolbar,
            },
            slotProps: {
              toolbar: {
                settings: toolbarSettings,
                onSettingsChange: setToolbarSettings,
                onRemoveFilter: handleRemoveFilter,
                showFilterBar,
                showSettings,
                customTitle: title || 'DataGrid',
              },
            },
          } : {
            slotProps: {
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                  debounceMs: 300,
                },
                printOptions: { disableToolbarButton: true },
              },
            },
          })}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            '& .group-header-row': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '& .MuiDataGrid-cell': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiDataGrid-toolbarContainer': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
          {...dataGridProps}
        />
      </Paper>
    </Stack>
  )
}

export default SelectableDataGrid